import { useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/UI";
import { toast } from "sonner";
import { TID } from "../constants/testIds";
import { Sparkles, Globe, AlertTriangle } from "lucide-react";

export default function EnterpriseIntelligence() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/analytics/enterprise-intelligence");
      setResult(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Enterprise Intelligence failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Enterprise Intelligence"
        subtitle="Cross-portfolio strategic intelligence. Reasons across tenants (Super Admin) or the entire workspace (Company Admin)."
        right={<button className="btn-primary" onClick={run} disabled={loading} data-testid={TID.godModeRunBtn}><Sparkles size={16} strokeWidth={2}/> {loading ? "Reasoning…" : "Run Analysis"}</button>}
      />

      <div className="ai-surface tracing-beam p-8 min-h-[400px]">
        {!result && !loading && (
          <div className="flex flex-col items-center justify-center h-80 text-center">
            <Globe size={56} className="text-blue-400 mb-4" strokeWidth={1.25}/>
            <div className="font-editorial text-3xl text-slate-100 mb-3">Awaiting activation</div>
            <div className="text-slate-400 max-w-md text-sm">
              Enterprise Intelligence analyzes every case in your portfolio and produces a board-ready strategic brief with hotspots, systemic risks, and executive recommendations , powered by PropHR AI.
            </div>
          </div>
        )}
        {loading && (
          <div className="flex flex-col items-center justify-center h-80">
            <div className="font-mono text-sm text-blue-300 mb-3">
              <span className="live-dot ai-dot mr-2 align-middle"></span> REASONING ACROSS PORTFOLIO...
            </div>
            <div className="text-slate-400 font-mono text-xs">PropHR AI · Dual-perspective evaluation engaged</div>
          </div>
        )}
        {result && (
          <div className="space-y-6">
            <div>
              <div className="eyebrow text-blue-300 mb-2">Executive Summary</div>
              <div className="font-editorial text-2xl text-slate-100 leading-relaxed">{result.executive_summary}</div>
            </div>

            {result.trend && (
              <div>
                <div className="eyebrow text-blue-300 mb-2">Trend</div>
                <div className="text-slate-200">{result.trend}</div>
              </div>
            )}

            {result.hotspots?.length > 0 && (
              <div>
                <div className="eyebrow text-blue-300 mb-3">Hotspots</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {result.hotspots.map((h, i) => (
                    <div key={i} className="border border-slate-800 bg-slate-950 p-4">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-slate-100 font-semibold">{h.department || ","}</div>
                        <span className={"pill " + (h.severity === "high" || h.severity === "critical" ? "pill-critical" : "pill-medium")}>{h.severity || ","}</span>
                      </div>
                      <div className="text-slate-400 text-sm">{h.issue}</div>
                      <div className="text-blue-400 font-mono text-xs mt-2">count: {h.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.systemic_risks?.length > 0 && (
              <div>
                <div className="eyebrow text-blue-300 mb-3 flex items-center gap-2"><AlertTriangle size={12} strokeWidth={2}/> Systemic Risks</div>
                <ul className="space-y-2">
                  {result.systemic_risks.map((r, i) => (
                    <li key={i} className="text-slate-200 flex gap-2"><span className="text-red-400">›</span> {r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.top_recommendations?.length > 0 && (
              <div>
                <div className="eyebrow text-blue-300 mb-3">Top Strategic Recommendations</div>
                <ol className="space-y-2">
                  {result.top_recommendations.map((r, i) => (
                    <li key={i} className="text-slate-200 flex gap-2"><span className="text-blue-400 font-mono">0{i+1}</span> {r}</li>
                  ))}
                </ol>
              </div>
            )}

            {result.iq200_deep_insight && (
              <div className="border border-blue-800 bg-slate-950 p-5 mt-6">
                <div className="eyebrow text-blue-300 mb-3">Sentinel Analytics · Hyper-Intelligent Synthesis</div>
                <div className="font-editorial text-xl text-slate-100 leading-relaxed">{result.iq200_deep_insight}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
